import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import Pagination from "react-bootstrap/Pagination";
import { getNewsItemsPerPage } from "../../api/api";
import { INews } from "../../types/news";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { IInnerProject } from "../../types/inner_page";

interface ProjectNewsShowcaseProps {
  limit?: number;
  pageItem: IInnerProject;
}

const ITEMS_PER_PAGE = 6;

const ProjectNewsShowcase: React.FC<ProjectNewsShowcaseProps> = ({ limit = ITEMS_PER_PAGE, pageItem }) => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { isLoading, error, data, refetch } = useQuery(
    ["NewsData", i18n.language, currentPage],
    () => getNewsItemsPerPage({ limit, language: i18n.language, page: currentPage, pageSize: ITEMS_PER_PAGE }),
    {
      onSuccess: (responseData) => {
        setTotalPages(Math.ceil(responseData.meta.pagination.total / ITEMS_PER_PAGE));
      },
    }
  );

  useEffect(() => {
    refetch();
  }, [i18n.language, currentPage, refetch]);

  if (isLoading) return <div>{t('misc.loading')}</div>;
  if (error) return <div>{t('news_showcase.error_loading')}</div>;

  return (
    <div>
      <Row className="tarabut-widget-row">
        <div className="tarabut-widget-content">
          <h3 className="text-center mb-3">{pageItem.attributes.projectNewsTitle}</h3>

          <Row xs={1} md={4} className="g-4 d-flex align-items-stretch">
            {data.data.map((newsItem: INews) => (
              <NewsItem key={newsItem.id} data={newsItem} />
            ))}
          </Row>

          {/* Pagination Controls */}
          {totalPages > 1 && <Pagination className="mt-3">
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, index) => (
              <Pagination.Item
                key={index + 1}
                active={index + 1 === currentPage}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            />
          </Pagination>}
        </div>
      </Row>


    </div>
  );
};

const NewsItem: React.FC<{ data: INews }> = ({ data }) => (
  <NewsCol>
    <SLink to={`/projects/news/${data.id}`}>
      <StyledCard className="shadow">
        <CardImage
          variant="top"
          src={`${process.env.REACT_APP_CMS}${data?.attributes.Images?.data?.[0]?.attributes?.formats?.thumbnail?.url ?? ''}`}

        />
        <CardTextContainer>
          <CardTitle dangerouslySetInnerHTML={{ __html: formatText(data.attributes.Title) }} />
          <CardText dangerouslySetInnerHTML={{ __html: formatText(data.attributes.SubTitle) }} />
        </CardTextContainer>
      </StyledCard>
    </SLink>
  </NewsCol>
);

// Helper function to format text, wrapping numbers in a styled <span>
const formatText = (text: string) => {
  if (!text) return '';
  return text.replace(/(\d+)/g, '<span class="en_font">$1</span>');
};

// Styled components
const StyledCard = styled(Card)`
  height: 100%;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  &:hover {
    transform: translateY(-4px);
  }
`;

const CardImage = styled(Card.Img)`
  height: 180px;
  object-fit: cover;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 14px;
`;

const CardTextContainer = styled(Card.Body)`
  display: flex;
  flex-direction: column;
  padding: 15px;
`;

const CardTitle = styled(Card.Title)`
  font-size: 16px;
  font-weight: bold;
  color: #000080;
`;

const CardText = styled(Card.Text)`
  font-size: 14px;
  color: #5a5a5a;
`;

const NewsCol = styled(Col)`
  display: flex;
  padding: 15px;
`;

const SLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-grow: 1;
`;

export default ProjectNewsShowcase;
